// =============================================================================
// « Parler à Matthieu » (T-241) — panneau de conversation latéral, depuis le Bureau (général) ou une fiche (ce mail).
// Matthieu répond en français et PROPOSE des actions du catalogue fermé ; chacune a son aperçu et se valide UNE PAR UNE
// (Valider / Corriger / Refuser) — aucune validation groupée. Le serveur exécute sous l'identité de celui qui valide.
// Sans clé Claude : « Matthieu n'est pas encore connecté », aucune fausse réponse. Les montants affichés sont LUS dans OmniHub.
// Tout texte (mails compris) est rendu en TEXTE BRUT : jamais de HTML injecté. Couleurs : tokens var(--oh-*) uniquement (2 modes).
// =============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import { OhField, OhInput, OhStatusNote } from "../../../components/ui/oh-charte";
import { TintedBadge } from "../../../components/ui/oh-tinted";
import Bouton from "./boutonBureau";
import * as api from "./matthieuApi";
import { agentApi } from "./bureauAmoApi";

export const NON_CONNECTE = "Matthieu n'est pas encore connecté";
// Moteur Agentic : la réponse n'arrive pas dans la requête, elle est relue toutes les 3 s pendant 2 min au plus. Au-delà : état explicite
// et bouton « Actualiser » (une lecture). ⛔ Aucun renvoi automatique du message.
export const INTERVALLE_LECTURE_MS = 3000;
export const DELAI_MAX_ATTENTE_MS = 120000;
const MOTEUR_LIBELLE = { omnihub: "OmniHub", agentic: "Agentic" };

const ETAT = {
  proposee: ["amber", "À valider"], corrigee: ["amber", "Corrigée, à valider"], validee: ["blue", "En cours d'exécution"],
  executee: ["green", "Exécutée"], refusee: ["red", "Refusée"], echec: ["red", "Échec"],
};
const EN_ATTENTE = ["proposee", "corrigee"];
const CHAMPS = {
  modifier_reponse: [["objet", "Objet", "input"], ["texte", "Texte de la réponse", "textarea"]],
  envoyer_reponse: [["texte", "Texte de la réponse", "textarea"]],
  classer_sans_repondre: [["motif", "Motif", "input"]],
  compte_rendu_dossier: [["texte", "Compte rendu", "textarea"]],
  enregistrer_regle: [["question", "Quand on voit", "input"], ["reponse", "Alors", "textarea"]],
};
const ROLE_STYLE = {
  utilisateur: { alignSelf: "flex-end", background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" },
  matthieu: { alignSelf: "flex-start", background: "var(--oh-bg-surface)", color: "var(--oh-text)", border: "0.5px solid var(--oh-border)" },
  systeme: { alignSelf: "center", background: "transparent", color: "var(--oh-text-muted)", border: "0.5px dashed var(--oh-border)" },
};
const AUTEUR = { utilisateur: "Vous", matthieu: "Matthieu", systeme: "OmniHub" };
const muted = { color: "var(--oh-text-muted)", fontSize: 13 };
const ZONE = { width: "100%", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "0.5px solid var(--oh-border)", background: "var(--oh-bg-elevated)", color: "var(--oh-text)", fontSize: 14, lineHeight: 1.6 };

const detail = (e, defaut) => (typeof e?.detail === "string" ? e.detail : defaut);

function CarteAction({ echangeId, a, occupe, onVue, onErreur, onExecutee }) {
  const [mode, setMode] = useState(null); // null | "corriger" | "refuser"
  const [valeurs, setValeurs] = useState({});
  const [motif, setMotif] = useState("");
  const [confirme, setConfirme] = useState(false);
  const [local, setLocal] = useState(false);
  const champs = CHAMPS[a.type];
  const enAttente = EN_ATTENTE.includes(a.etat);
  const bloque = occupe || local;
  const ap = a.apercu || {};
  const [couleur, libelle] = ETAT[a.etat] || ["blue", a.etat];
  const adresse = ap.confirmation_destinataire;
  const id = a.id;

  const lancer = async (appel, apres) => {
    onErreur(null);
    setLocal(true);
    try {
      const r = await appel();
      onVue(r);
      apres?.(r);
      setMode(null);
    } catch (e) {
      onErreur(detail(e, "L'action n'a pas pu être traitée."), e?.status === 409);
    } finally {
      setLocal(false);
    }
  };

  return (
    <div data-testid={`matthieu-action-${id}`} style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)", padding: "var(--oh-space-4)" }} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p style={{ margin: 0, fontWeight: 500, color: "var(--oh-text)", fontSize: 14 }}>{ap.titre || a.type}</p>
        <TintedBadge color={couleur} data-testid={`matthieu-action-etat-${id}`}>{libelle}</TintedBadge>
      </div>
      {(ap.lignes || []).length > 0 && (
        <dl style={{ margin: 0, fontSize: 13, color: "var(--oh-text)" }} className="space-y-1">
          {ap.lignes.map(([k, v], i) => (
            <div key={i} className="flex flex-wrap gap-2"><dt style={{ ...muted, minWidth: 110 }}>{k}</dt><dd style={{ margin: 0, overflowWrap: "anywhere" }}>{String(v)}</dd></div>
          ))}
        </dl>
      )}
      {ap.texte && (
        <pre data-testid={`matthieu-action-texte-${id}`} style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", fontSize: 14, color: "var(--oh-text)", background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", padding: "var(--oh-space-3)", overflowWrap: "anywhere" }}>{ap.texte}</pre>
      )}
      {(ap.avertissements || []).map((w, i) => <OhStatusNote key={i} variant="info">{w}</OhStatusNote>)}
      {ap.irreversible && enAttente && <p style={{ ...muted, margin: 0 }}>Cette action ne se reprend pas une fois validée.</p>}
      {a.etat === "refusee" && a.motif && <p style={{ ...muted, margin: 0 }}>Motif : {a.motif}</p>}

      {enAttente && mode === null && (
        <div className="space-y-2">
          {adresse && (
            <label className="flex items-start gap-2" style={{ fontSize: 14, color: "var(--oh-text)" }}>
              <input type="checkbox" data-testid={`matthieu-confirme-${id}`} checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
              <span>Je confirme l'envoi à {adresse}</span>
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <Bouton type="secondaire" data-testid={`matthieu-valider-${id}`} disabled={bloque || (Boolean(adresse) && !confirme)}
              onClick={() => lancer(() => api.validerAction(echangeId, id, a.version, Boolean(adresse) && confirme), (r) => onExecutee(r))}>
              {a.type === "envoyer_reponse" ? "Valider et envoyer" : "Valider"}
            </Bouton>
            {champs && (
              <Bouton data-testid={`matthieu-corriger-${id}`} disabled={bloque}
                onClick={() => { setValeurs(Object.fromEntries(champs.map(([c]) => [c, a.parametres?.[c] || ""]))); setMode("corriger"); }}>Corriger</Bouton>
            )}
            <Bouton data-testid={`matthieu-refuser-${id}`} disabled={bloque} onClick={() => { setMotif(""); setMode("refuser"); }}>Refuser</Bouton>
          </div>
        </div>
      )}

      {enAttente && mode === "corriger" && champs && (
        <div className="space-y-2" data-testid={`matthieu-correction-${id}`}>
          {champs.map(([c, libelleChamp, genre]) => (
            <OhField key={c} label={libelleChamp} htmlFor={`matthieu-champ-${id}-${c}`}>
              {genre === "textarea"
                ? <textarea id={`matthieu-champ-${id}-${c}`} data-testid={`matthieu-champ-${id}-${c}`} rows={6} value={valeurs[c] || ""} style={ZONE}
                    onChange={(e) => setValeurs((v) => ({ ...v, [c]: e.target.value }))} />
                : <OhInput id={`matthieu-champ-${id}-${c}`} data-testid={`matthieu-champ-${id}-${c}`} value={valeurs[c] || ""}
                    onChange={(e) => setValeurs((v) => ({ ...v, [c]: e.target.value }))} />}
            </OhField>
          ))}
          <div className="flex flex-wrap gap-2">
            <Bouton type="secondaire" data-testid={`matthieu-correction-enregistrer-${id}`} disabled={bloque}
              onClick={() => lancer(() => api.corrigerAction(echangeId, id, a.version,
                Object.fromEntries(Object.entries(valeurs).filter(([, v]) => String(v).trim() !== "").map(([k, v]) => [k, String(v).trim()]))))}>
              Enregistrer la correction
            </Bouton>
            <Bouton data-testid={`matthieu-correction-annuler-${id}`} onClick={() => setMode(null)}>Annuler</Bouton>
          </div>
          <p style={{ ...muted, margin: 0 }}>Corriger n'exécute rien : vous validerez ensuite la version corrigée.</p>
        </div>
      )}

      {enAttente && mode === "refuser" && (
        <div className="space-y-2" data-testid={`matthieu-refus-${id}`}>
          <OhField label="Motif du refus" htmlFor={`matthieu-motif-${id}`} hint="Entre 3 et 500 caractères.">
            <OhInput id={`matthieu-motif-${id}`} data-testid={`matthieu-motif-${id}`} value={motif} maxLength={500} onChange={(e) => setMotif(e.target.value)} />
          </OhField>
          <div className="flex flex-wrap gap-2">
            <Bouton data-testid={`matthieu-refus-confirmer-${id}`} disabled={bloque || motif.trim().length < 3}
              onClick={() => lancer(() => api.refuserAction(echangeId, id, a.version, motif.trim()))}>Confirmer le refus</Bouton>
            <Bouton data-testid={`matthieu-refus-annuler-${id}`} onClick={() => setMode(null)}>Annuler</Bouton>
          </div>
        </div>
      )}
    </div>
  );
}

// `messageInitial` : envoyé UNE fois, à l'ouverture, quand Matthieu est connecté et la conversation vide (T-276 : « Rédiger une réponse avec Matthieu »).
// T-406 : « Demander à Chloé / à Joey » = CE panneau, réutilisé. Chloé et Joey n'ont pas (encore) d'action proposée dans la conversation : seuls la lecture
// et l'envoi d'un message existent ; sans moteur IA branché le serveur dit « <Agent> n'est pas encore branchée à l'IA » (jamais une erreur technique).
const NOMS = { matthieu: "Matthieu", chloe: "Chloé", joey: "Joey" };
const adaptateur = (a) => ({
  lireEchange: (type, id) => a.lireEchange(type === "dossier" ? id : undefined),
  envoyerMessage: (cible, texte) => a.envoyerMessage(texte, cible?.type === "dossier" ? cible.id : undefined),
});

export default function ParlerAMatthieu({ cible, titre, onFerme, onExecutee, messageInitial, agent = "matthieu" }) {
  const estM = agent === "matthieu";
  const nom = NOMS[agent] || "l'agent";
  const L = estM ? api : adaptateur(agentApi(agent));
  titre = titre || `Parler à ${nom}`;
  const [etat, setEtat] = useState({ chargement: true, erreur: null, echange: null, connecte: false, moteur: "omnihub", message: null });
  const [delaiDepasse, setDelaiDepasse] = useState(false);
  const [actualise, setActualise] = useState(false);
  const [texte, setTexte] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const fin = useRef(null);
  const amorce = useRef(false);

  const appliquer = useCallback((r) => setEtat({ chargement: false, erreur: null, echange: r.echange, connecte: Boolean(r.connecte), moteur: r.moteur === "agentic" ? "agentic" : "omnihub", message: r.message_non_connecte || null }), []);

  const recharger = useCallback(async () => {
    try { appliquer(await L.lireEchange(cible.type, cible.id)); }
    catch (e) { setEtat({ chargement: false, erreur: detail(e, "La conversation n'a pas pu être chargée."), echange: null, connecte: false, moteur: "omnihub" }); }
  }, [cible.type, cible.id, appliquer]);

  useEffect(() => { setEtat({ chargement: true, erreur: null, echange: null, connecte: false, moteur: "omnihub" }); recharger(); }, [recharger]);

  // Une réponse est attendue (moteur Agentic) tant que le registre dit « attente = matthieu ».
  const attend = etat.echange?.attente === agent;
  useEffect(() => {
    setDelaiDepasse(false);
    if (!attend) return undefined;
    const debut = Date.now(); // l'échéance se mesure ici, à la première observation de l'attente : pas d'écart d'horloge avec le serveur
    let actif = true;
    let minuteur;
    const tour = () => {
      minuteur = setTimeout(async () => {
        if (!actif) return;
        try { const r = await L.lireEchange(cible.type, cible.id); if (actif) appliquer(r); } catch { /* lecture silencieuse : la prochaine passe réessaie */ }
        if (!actif) return;
        if (Date.now() - debut >= DELAI_MAX_ATTENTE_MS) setDelaiDepasse(true);
        else tour();
      }, INTERVALLE_LECTURE_MS);
    };
    tour();
    return () => { actif = false; clearTimeout(minuteur); };
  }, [attend, cible.type, cible.id, appliquer]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onFerme?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onFerme]);
  const nbMessages = etat.echange?.messages?.length || 0;
  useEffect(() => { if (fin.current && fin.current.scrollIntoView) fin.current.scrollIntoView({ block: "end" }); }, [nbMessages, occupe]);

  const echange = etat.echange;
  const actions = echange?.actions || [];
  const aValider = actions.filter((a) => EN_ATTENTE.includes(a.etat)).length;

  const envoyer = async (initial) => {
    const t = (typeof initial === "string" ? initial : texte).trim();
    if (!t || occupe) return;
    setOccupe(true);
    setErreur(null);
    try {
      appliquer(await L.envoyerMessage(cible, t));
      setTexte("");
    } catch (e) {
      setErreur(detail(e, "Le message n'a pas pu être envoyé."));
      if (e?.status === 502 || e?.status === 409) recharger();
    } finally {
      setOccupe(false);
    }
  };

  useEffect(() => {
    if (!messageInitial || amorce.current || etat.chargement || etat.erreur || !etat.connecte || !etat.echange) return;
    if ((etat.echange.messages || []).length > 0) return;
    amorce.current = true;
    envoyer(messageInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageInitial, etat]);

  const surErreurAction = (msg, perime) => {
    setErreur(msg && perime ? `${msg} L'historique est rechargé.` : msg);
    if (perime) recharger();
  };

  const actualiser = async () => {
    setActualise(true);
    setErreur(null);
    try { appliquer(await L.lireEchange(cible.type, cible.id)); }
    catch (e) { setErreur(detail(e, "La conversation n'a pas pu être actualisée.")); }
    finally { setActualise(false); }
  };

  // En mode Agentic la clé OmniHub n'intervient pas : l'absence de clé n'est pas un blocage.
  const nonConnecte = !etat.chargement && !etat.erreur && !etat.connecte && etat.moteur !== "agentic";
  const reflechit = occupe || (attend && !delaiDepasse);

  return (
    <aside role="dialog" aria-label={`Conversation avec ${nom}`} data-testid="matthieu-panneau" className="flex flex-col"
      style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(440px, 100vw)", zIndex: 60, background: "var(--oh-bg)", borderLeft: "0.5px solid var(--oh-border)" }}>
      <header className="flex items-start justify-between gap-2" style={{ padding: "var(--oh-space-4)", borderBottom: "0.5px solid var(--oh-border)" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: "var(--oh-text)" }}>{titre}</h3>
          <p style={{ ...muted, margin: 0 }} data-testid="matthieu-a-valider">{aValider > 0 ? `${aValider} action${aValider > 1 ? "s" : ""} à valider` : `${nom} propose, vous validez.`}</p>
        </div>
        <Bouton aria-label="Fermer la conversation" data-testid="matthieu-fermer" onClick={onFerme} style={{ padding: "0 var(--oh-space-2)" }}><X className="w-4 h-4" /></Bouton>
      </header>

      <div className="flex-1 overflow-auto space-y-3" style={{ padding: "var(--oh-space-4)" }} data-testid="matthieu-corps">
        {etat.chargement && <p style={muted} data-testid="matthieu-chargement">Chargement de la conversation…</p>}
        {etat.erreur && <OhStatusNote variant="error" data-testid="matthieu-erreur-chargement" title="Conversation indisponible">{etat.erreur}</OhStatusNote>}
        {nonConnecte && (
          <OhStatusNote variant="info" data-testid="matthieu-non-connecte" title={estM ? NON_CONNECTE : (etat.message || `${nom} n'est pas encore branchée à l'IA`)}>
            {estM
              ? "Le super administrateur doit renseigner la clé Claude dans Intégrations (ou activer le moteur Agentic). Tant que ce n'est pas fait, Matthieu ne répond pas et rien n'est enregistré."
              : `Le super administrateur doit activer le moteur de ${nom}. Tant que ce n'est pas fait, ${nom} ne répond pas et rien n'est enregistré.`}
          </OhStatusNote>
        )}
        {echange && nbMessages === 0 && !nonConnecte && (
          <p style={muted} data-testid="matthieu-vide">
            {estM
              ? "Posez votre question : « résume-moi ce mail », « que dois-je répondre ? », ou donnez une règle : « ce type de mail, tu le classes dans DRIHL ANAH »."
              : `Posez votre question sur un dossier de la compétence de ${nom}. ${nom} ne répond que sur ce domaine.`}
          </p>
        )}
        {echange && (
          <div className="flex flex-col gap-2" data-testid="matthieu-messages" aria-live="polite">
            {echange.messages.map((m) => (
              <div key={m.id} data-testid={`matthieu-message-${m.role}`} style={{ ...(ROLE_STYLE[m.role] || ROLE_STYLE.matthieu), maxWidth: "92%", borderRadius: "var(--oh-radius-card)", padding: "var(--oh-space-3)" }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 500 }} className="flex items-center justify-between gap-2">
                  <span>{AUTEUR[m.role] || (m.role === agent ? nom : m.role)}</span>
                  {m.role === agent && MOTEUR_LIBELLE[m.moteur] && (
                    <span data-testid={`matthieu-marque-moteur-${m.id}`} title={`Réponse produite par le moteur ${MOTEUR_LIBELLE[m.moteur]}`} style={{ fontWeight: 400, fontSize: 12, color: "var(--oh-text-secondary)" }}>{MOTEUR_LIBELLE[m.moteur]}</span>
                  )}
                </p>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{m.texte}</p>
              </div>
            ))}
          </div>
        )}
        {reflechit && <p style={muted} role="status" data-testid="matthieu-reflechit">{nom} réfléchit…</p>}
        {attend && delaiDepasse && (
          <div className="space-y-2" data-testid="matthieu-delai">
            <OhStatusNote variant="info" title={`${nom} n'a pas encore répondu`}>
              Votre message est bien enregistré, il n'est pas renvoyé. Actualisez pour voir si la réponse est arrivée.
            </OhStatusNote>
            <Bouton data-testid="matthieu-actualiser" disabled={actualise} onClick={actualiser}>Actualiser</Bouton>
          </div>
        )}
        {actions.length > 0 && (
          <section className="space-y-2" aria-label="Actions proposées" data-testid="matthieu-actions">
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--oh-text)" }}>Actions proposées</h4>
            {actions.map((a) => (
              <CarteAction key={a.id} echangeId={echange.id} a={a} occupe={occupe} onVue={appliquer} onErreur={surErreurAction} onExecutee={(r) => onExecutee?.(r)} />
            ))}
          </section>
        )}
        <div ref={fin} />
      </div>

      <footer className="space-y-2" style={{ padding: "var(--oh-space-4)", borderTop: "0.5px solid var(--oh-border)" }}>
        {erreur && <OhStatusNote variant="error" data-testid="matthieu-erreur" role="alert">{erreur}</OhStatusNote>}
        <label htmlFor="matthieu-texte" style={{ fontSize: 14, fontWeight: 500, color: "var(--oh-text)" }}>Votre message</label>
        <textarea id="matthieu-texte" data-testid="matthieu-texte" rows={3} value={texte} maxLength={4000} disabled={occupe || nonConnecte || Boolean(etat.erreur) || etat.chargement}
          placeholder={nonConnecte ? (estM ? NON_CONNECTE : (etat.message || `${nom} n'est pas encore branchée à l'IA`)) : `Écrivez à ${nom}…`} style={ZONE}
          onChange={(e) => setTexte(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); envoyer(); } }} />
        <div className="flex items-center justify-between gap-2">
          <span style={muted}>Ctrl + Entrée pour envoyer</span>
          <Bouton type="secondaire" data-testid="matthieu-envoyer" disabled={occupe || nonConnecte || Boolean(etat.erreur) || !texte.trim()} onClick={envoyer}>Envoyer à {nom}</Bouton>
        </div>
      </footer>
    </aside>
  );
}
